/**
 * WebSocket service for Gaia Assistant custom protocol.
 *
 * Client → Agent: SEND_MESSAGE, USER_STATE_SNAPSHOT, TOOL_CONFIRMATION_RESPONSE, REQUEST_MESSAGE_HISTORY_SNAPSHOT, FILE_ATTACHMENT_UPLOADED, AGENT_ACTION_REVIEW_COMPLETED
 * Agent → Client: TEXT_START, TEXT_DELTA, TEXT_END, REQUEST_USER_STATE_SNAPSHOT, TOOL_CONFIRMATION_REQUEST, MESSAGE_HISTORY_SNAPSHOT, AWAIT_AGENT_ACTION_REVIEW
 */

import { getAuthToken } from "features/auth/utils";
import { GaiaConstants } from "utils";
import { MessageRole, SourceMetadata } from "features/assistant/definitions";

export type { Citation, SourceMetadata } from "features/assistant/definitions";

export interface TextStartEvent {
  type: "TEXT_START";
  message_id: string;
  source_metadata?: SourceMetadata;
}

export interface TextDeltaEvent {
  type: "TEXT_DELTA";
  message_id: string;
  delta: string;
  source_metadata?: SourceMetadata;
}

export interface TextEndEvent {
  type: "TEXT_END";
  message_id: string;
  source_metadata?: SourceMetadata;
}

export interface StepEvent {
  type: "STEP";
  message_id: string;
  step_name: string;
  status: "in_progress" | "completed" | "failed";
}

export interface ToolConfirmationRequestEvent {
  type: "TOOL_CONFIRMATION_REQUEST";
  request_id: string;
  tool_name: string;
  description: string;
}

export interface RequestUserStateSnapshotEvent {
  type: "REQUEST_USER_STATE_SNAPSHOT";
}

export interface HistoryContentBlock {
  type: "text" | "tool_use";
  text?: string;
  display_text?: string;
  tool_name?: string;
  description?: string;
  status?: "completed" | "denied" | "error";
  // Source attributes of a text block in persisted history. Absent ⇒ "assistant".
  source_metadata?: SourceMetadata;
  // A file-upload turn: the raw <user_context> text is omitted; render a file chip instead.
  is_file_attachment?: boolean;
  file_name?: string | null;
  // A prompt raised on the user's behalf; kept out of the transcript on replay.
  hidden?: boolean;
}

export interface HistoryMessage {
  role: MessageRole;
  content: HistoryContentBlock[];
}

export interface SessionMetadata {
  feedback?: "THUMBS_UP" | "THUMBS_DOWN" | null;
}

export interface MessageHistorySnapshotEvent {
  type: "MESSAGE_HISTORY_SNAPSHOT";
  messages: HistoryMessage[];
  session_metadata?: SessionMetadata;
}

export interface ToolCallStartEvent {
  type: "TOOL_CALL_START";
  message_id: string;
  tool_use_id: string;
  tool_name: string;
}

export interface ToolCallEndEvent {
  type: "TOOL_CALL_END";
  message_id: string;
  tool_use_id: string;
  tool_name: string;
  status: "completed" | "error";
  result: string;
}

export interface ErrorEvent {
  type: "ERROR";
  message: string;
}

/**
 * The agent asking to be told once this review is no longer pending, so it can resume the
 * conversation without the user having to prompt it again.
 */
export interface AwaitAgentActionReviewEvent {
  type: "AWAIT_AGENT_ACTION_REVIEW";
  review_id: string;
}

export type ServerEvent =
  | TextStartEvent
  | TextDeltaEvent
  | TextEndEvent
  | StepEvent
  | ToolCallStartEvent
  | ToolCallEndEvent
  | ToolConfirmationRequestEvent
  | RequestUserStateSnapshotEvent
  | MessageHistorySnapshotEvent
  | AwaitAgentActionReviewEvent
  | ErrorEvent;

export type ServerEventHandler = (event: ServerEvent) => void;

export class AssistantWebSocket {
  private ws: WebSocket | null = null;
  private onEvent: ServerEventHandler;
  private onConnectionChange: (connected: boolean) => void;

  constructor(
    onEvent: ServerEventHandler,
    onConnectionChange: (connected: boolean) => void,
  ) {
    this.onEvent = onEvent;
    this.onConnectionChange = onConnectionChange;
  }

  connect(url: string): void {
    this.disconnect();

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log("Assistant WebSocket connected:", url);
        this.onConnectionChange(true);
        this.sendUserStateSnapshot();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const parsed = JSON.parse(event.data) as ServerEvent;

          if (parsed.type === "REQUEST_USER_STATE_SNAPSHOT") {
            this.sendUserStateSnapshot();
            return;
          }

          this.onEvent(parsed);
        } catch {
          console.error("Failed to parse server event:", event.data);
        }
      };

      this.ws.onclose = (event: CloseEvent) => {
        console.log(
          `Assistant WebSocket closed: code=${event.code} reason="${event.reason}" wasClean=${event.wasClean}`,
        );
        this.onConnectionChange(false);
      };

      this.ws.onerror = (event: Event) => {
        console.error("WebSocket error:", event);
      };
    } catch (err) {
      console.error("Failed to create WebSocket:", err);
      this.onConnectionChange(false);
    }
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
  }

  sendMessage(prompt: string, displayText?: string): void {
    this.send({
      type: "SEND_MESSAGE",
      prompt,
      ...(displayText && { display_text: displayText }),
    });
  }

  async sendUserStateSnapshot(): Promise<void> {
    const jwtToken = await getAuthToken();
    const teamName =
      localStorage.getItem(GaiaConstants.SELECTED_TEAM_NAME) || "";
    const timezoneCode = Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.send({
      type: "USER_STATE_SNAPSHOT",
      state: {
        jwt_token: jwtToken,
        current_team_name: teamName,
        timezone_code: timezoneCode,
      },
    });
  }

  sendToolConfirmationResponse(requestId: string, confirmed: boolean): void {
    this.send({
      type: "TOOL_CONFIRMATION_RESPONSE",
      request_id: requestId,
      confirmed,
    });
  }

  sendRequestMessageHistory(): void {
    this.send({ type: "REQUEST_MESSAGE_HISTORY_SNAPSHOT" });
  }

  sendInterruptResponse(): void {
    this.send({ type: "INTERRUPT_RESPONSE" });
  }

  sendFeedback(feedback: "THUMBS_UP" | "THUMBS_DOWN"): void {
    this.send({ type: "SUBMIT_FEEDBACK", feedback });
  }

  sendFileAttachmentUploaded(attachmentId: string): void {
    this.send({
      type: "FILE_ATTACHMENT_UPLOADED",
      attachment_id: attachmentId,
    });
  }

  /**
   * Reports only that the review has settled, never the decisions — the agent reads those from
   * the service so a stale client cannot drive a task change. No-ops while disconnected.
   */
  sendAgentActionReviewCompleted(reviewId: string): void {
    this.send({
      type: "AGENT_ACTION_REVIEW_COMPLETED",
      review_id: reviewId,
    });
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private send(data: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }
}
